// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useActionState, useCallback, type ReactNode, useRef, useState, useSyncExternalStore } from 'react';

// oxlint-disable-next-line no-restricted-imports
import { setLocalStorage, getLocalStorage, removeLocalStorage } from '@evolvemep/api-ts';

import type { LOCAL_STORAGE_KEY, LOCAL_STORAGE_TYPE } from 'constants/globalConstants';
import { isNotNil } from 'helpers/isNotNil';
import { useGeneralContext } from 'helpers/useGeneralContext';

type Listener = () => void;

type LocalStorageStore = {
  getValue: <K extends LOCAL_STORAGE_KEY>(
    key: K,
    fallback?: LOCAL_STORAGE_TYPE[K],
  ) => LOCAL_STORAGE_TYPE[K] | undefined;
  setValue: <K extends LOCAL_STORAGE_KEY>(key: K, value: LOCAL_STORAGE_TYPE[K]) => void;
  removeValue: <K extends LOCAL_STORAGE_KEY>(key: K) => void;
  subscribe: (key: LOCAL_STORAGE_KEY, listener: Listener) => () => void;
};

const createLocalStorageStore = (): LocalStorageStore => {
  const data = new Map<LOCAL_STORAGE_KEY, unknown>();
  const listeners = new Map<LOCAL_STORAGE_KEY, Set<Listener>>();

  const notifyListeners = (key: LOCAL_STORAGE_KEY) => {
    const keyListeners = listeners.get(key);
    if (keyListeners) {
      keyListeners.forEach((listener) => listener());
    }
  };

  return {
    getValue: (key, fallback) => {
      if (data.has(key)) {
        return data.get(key) as LOCAL_STORAGE_TYPE[typeof key];
      }
      // Only cache values that are actually in storage; caching `fallback` would make a missing key look stored.
      const storageValue = getLocalStorage<typeof key, LOCAL_STORAGE_TYPE[typeof key]>(key);
      if (storageValue === undefined) return fallback;
      data.set(key, storageValue);
      return storageValue;
    },
    setValue: (key, value) => {
      data.set(key, value);
      setLocalStorage(key, value, true);
      notifyListeners(key);
    },
    removeValue: (key) => {
      data.delete(key);
      removeLocalStorage(key);
      notifyListeners(key);
    },
    subscribe: (key, listener) => {
      if (!listeners.has(key)) {
        listeners.set(key, new Set());
      }
      listeners.get(key)?.add(listener);
      return () => {
        const keyListeners = listeners.get(key);
        if (keyListeners) {
          keyListeners.delete(listener);
          if (keyListeners.size === 0) {
            listeners.delete(key);
          }
        }
      };
    },
  };
};

const LocalStorageContext = React.createContext<LocalStorageStore | undefined>(undefined);

export const LocalStorageProvider = ({ children }: { children: ReactNode }) => {
  const [store] = useState(createLocalStorageStore);
  return <LocalStorageContext value={store}>{children}</LocalStorageContext>;
};

export const getLocalStorageWrapped = <K extends LOCAL_STORAGE_KEY>(key: K, initialValue?: LOCAL_STORAGE_TYPE[K]) =>
  getLocalStorage<K, LOCAL_STORAGE_TYPE[K]>(key, initialValue);

export const setLocalStorageWrapped = <K extends LOCAL_STORAGE_KEY>(key: K, value: LOCAL_STORAGE_TYPE[K]) =>
  setLocalStorage<K, LOCAL_STORAGE_TYPE[K]>(key, value);

/**
 * Type-safe local storage handler.
 * @param key The local storage key name
 * @param initialValue The value to return if no value found in storage
 */
export const useLocalStorage = <K extends LOCAL_STORAGE_KEY>(key: K, initialValue: LOCAL_STORAGE_TYPE[K]) => {
  const store = useGeneralContext(LocalStorageContext, 'LocalStorage');
  const initialValueRef = useRef(initialValue);

  const subscribe = useCallback((onStoreChange: () => void) => store.subscribe(key, onStoreChange), [store, key]);

  const getSnapshot = useCallback(
    () => store.getValue(key, initialValueRef.current) ?? initialValueRef.current,
    [store, key],
  );

  const storedValue = useSyncExternalStore(subscribe, getSnapshot);

  const getHasStoredValueSnapshot = useCallback(() => isNotNil(store.getValue(key)), [store, key]);
  const hasStoredValue = useSyncExternalStore(subscribe, getHasStoredValueSnapshot);

  const setValueInner = useCallback(
    (value: LOCAL_STORAGE_TYPE[K] | ((value: LOCAL_STORAGE_TYPE[K]) => LOCAL_STORAGE_TYPE[K])) => {
      const currentValue = store.getValue(key, initialValueRef.current) ?? initialValueRef.current;
      const valueToStore = value instanceof Function ? value(currentValue) : value;
      store.setValue(key, valueToStore);
    },
    [key, store],
  );

  return [storedValue, setValueInner, hasStoredValue] as const;
};

/**
 * Note: This hook does not use the LocalStorageContext,
 * so the value will not be updated when the value for this key is updated externally.
 */
export const useActionLocalStorage = <K extends LOCAL_STORAGE_KEY, T>(
  key: K,
  action: Parameters<typeof useActionState<LOCAL_STORAGE_TYPE[K], T>>[0],
  initialValue: Parameters<typeof useActionState<LOCAL_STORAGE_TYPE[K], T>>[1],
) => {
  const innerAction = useCallback<typeof action>(
    (...params) => {
      const result = action(...params);
      void (async () => result)().then((value) => setLocalStorage(key, value, true));
      return result;
    },
    [action, key],
  );
  return useActionState<LOCAL_STORAGE_TYPE[K], T>(
    innerAction,
    (getLocalStorage(key, initialValue) ?? initialValue) as typeof initialValue,
  );
};
